import { Link as RouterLink, useLocation, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Button,
  Card,
  CardActionArea,
  CircularProgress,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import { formatMoney } from "@bw/shared/utils";
import type { ClubCoachProfilePublicResponse } from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import { useCoaches } from "../hooks/useCoaches";
import {
  readFacilityDiscoveryReturn,
  withFacilityDiscoveryReturn,
} from "../../../common/facilityNavigation";

/**
 * CoachesListPage (/coaches) — discover-coaches list, reached THROUGH the Play
 * hub (no new top-level nav). Reads the coach list (GET /coaches → {items,
 * total}) and renders a link card per coach.
 *
 * Each card shows ONLY CoachProfileResponse-backed facts: the photo (an HONEST
 * fallback when photo_url is null — never a broken <img>), the display_name, the
 * specializations, and years_experience. The whole card LINKS to the detail
 * (/coaches/{user_id}) — the uuid is the link target, never visible body text.
 *
 * Honesty: no rating / ★ / review count is invented (the profile carries none);
 * a raw user_id uuid is never rendered as visible text. No toast lib — loading is
 * role="status", error is role="alert", and the empty state is "No coaches yet".
 *
 * The AppShell supplies no page title, so this routed page OWNS its single <h1>
 * via <Page title="Coaches">.
 */
export function CoachesListPage() {
  const { facilityId } = useParams<{ facilityId: string }>();
  const location = useLocation();
  const facilitySearchReturn = readFacilityDiscoveryReturn(location.search);
  const { data, isLoading, isError, refetch } = useCoaches({ facilityId });
  const coaches = data ?? [];

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <Stack
        direction="row"
        spacing={1.5}
        role="status"
        sx={{ alignItems: "center" }}
      >
        <CircularProgress size={20} />
        <Typography color="text.secondary">Loading coaches…</Typography>
      </Stack>
    );
  } else if (isError) {
    body = (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void refetch()}>
            Retry
          </Button>
        }
      >
        We couldn&apos;t load coaches. Please try again.
      </Alert>
    );
  } else if (coaches.length === 0) {
    body = <Typography color="text.secondary">No coaches yet</Typography>;
  } else {
    body = (
      <List disablePadding>
        {coaches.map((coach) => (
          <CoachCard
            key={coach.coach_profile_id}
            coach={coach}
            facilityId={facilityId}
            facilitySearchReturn={facilitySearchReturn}
          />
        ))}
      </List>
    );
  }

  return (
    <Page title="Coaches">
      <Stack spacing={2}>
        {facilityId ? (
          <Button
            component={RouterLink}
            to={withFacilityDiscoveryReturn(
              `/facility/${encodeURIComponent(facilityId)}`,
              facilitySearchReturn,
            )}
            sx={{ alignSelf: "flex-start" }}
          >
            Back to facility
          </Button>
        ) : null}
        {body}
      </Stack>
    </Page>
  );
}

/**
 * Initials fallback for a coach with no photo_url (never a broken <img>).
 *
 * NOTE: intentionally NOT the shared `common/avatar.ts` `initials()`. The two use
 * different rules — this coach mark takes the FIRST TWO words' initials
 * ("No Photo Coach" → "NP", "Lin" → "L"), whereas the shared util takes the
 * FIRST + LAST initial ("No Photo Coach" → "NC", "Lin" → "LI"). Folding to the
 * shared util would silently change the rendered mark for multi-word / single-word
 * coach names, so the local rule is kept deliberately. (Reviewed: P3 fold declined
 * because the behavior diverges — clean-migration only folds identical behavior.)
 */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

function CoachCard({
  coach,
  facilityId,
  facilitySearchReturn,
}: {
  coach: ClubCoachProfilePublicResponse;
  facilityId?: string;
  facilitySearchReturn?: string;
}) {
  const specializations = coach.specializations ?? [];
  const binding = facilityId
    ? coach.facilities.find((item) => item.facility_id === facilityId)
    : coach.facilities.length === 1
      ? coach.facilities[0]
      : undefined;
  return (
    <ListItem component="li" disableGutters sx={{ mb: 1.5 }}>
      <Card variant="outlined" sx={{ width: "100%" }}>
        <CardActionArea
          component={RouterLink}
          to={withFacilityDiscoveryReturn(
            `/coaches/${coach.coach_profile_id}${facilityId ? `?facility_id=${encodeURIComponent(facilityId)}` : ""}`,
            facilitySearchReturn,
          )}
          aria-label={coach.display_name}
          sx={{ p: 2 }}
        >
          <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
            <Avatar
              src={coach.photo_url ?? undefined}
              alt={coach.photo_url ? coach.display_name : ""}
              aria-hidden={coach.photo_url ? undefined : true}
              sx={{ width: 56, height: 56, bgcolor: "action.selected" }}
            >
              {initials(coach.display_name)}
            </Avatar>
            <Stack sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }}>
                {coach.display_name}
              </Typography>
              {specializations.length > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {specializations.join(" · ")}
                </Typography>
              ) : null}
              {binding?.hourly_rate != null ? (
                <Typography variant="body2" color="text.secondary">
                  {formatMoney(binding.hourly_rate, binding.currency)} per hour
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        </CardActionArea>
        <Button
          component={RouterLink}
          to={withFacilityDiscoveryReturn(
            `/coaches/${coach.coach_profile_id}?${new URLSearchParams({ ...(facilityId || binding?.facility_id ? { facility_id: facilityId ?? binding!.facility_id } : {}), message: "1" }).toString()}`,
            facilitySearchReturn,
          )}
          sx={{ mx: 2, mb: 1.5 }}
        >
          Message coach
        </Button>
      </Card>
    </ListItem>
  );
}
